<template>
    <CCardBox
        class="CCollapsible"
        :class="{ 'flex flex-col-reverse': reverseColumn }"
        :plain="plain"
        variant="nopadding"
    >
        <div
            class="relative flex cursor-pointer items-center"
            :class="{ 'px-4 py-3 md:py-4': withPadding }"
            @click="toggle()"
        >
            <div class="pr-4" :class="{ 'flex-1': !chevronNextToText }">
                <slot v-if="$slots.headerExpanded && expanded" name="headerExpanded" />
                <slot v-else name="header" />
            </div>

            <div v-if="$slots.button" class="hidden pr-4 sm:block">
                <slot name="button" />
            </div>

            <CIcon
                v-if="!chevronHidden"
                class="size-6 opacity-50"
                :class="chevronClass"
                :name="expanded ? 'dropdown-up' : 'dropdown-down'"
            />
        </div>

        <transition
            enter-active-class="transition duration-300 ease-in delay-75"
            :enter-class="reverseColumn ? 'translate-y-4 opacity-0' : '-translate-y-4 opacity-0'"
            leave-active-class="transition duration-300 ease-in"
            :leave-to-class="reverseColumn ? 'translate-y-4 opacity-0' : '-translate-y-4 opacity-0'"
        >
            <div v-show="expanded" :class="{ 'px-4 pb-5': withPadding }">
                <template v-if="withPadding">
                    <template v-if="$slots.button">
                        <CLineBreak class="-mx-4 my-0 sm:hidden" />
                        <div class="my-4 sm:hidden">
                            <slot name="button" />
                        </div>
                    </template>
                    <CLineBreak class="-mx-4 mb-5 mt-0" />
                </template>
                <slot />
            </div>
        </transition>
    </CCardBox>
</template>

<script setup lang="ts">
import CCardBox from '@/components/ui/CCardBox.vue'
import CIcon from '@/components/ui/CIcon.vue'
import CLineBreak from '@/components/ui/CLineBreak.vue'
import { ref, computed, watch } from 'vue'

interface Props {
    modelValue?: boolean
    plain?: boolean
    reverseColumn?: boolean
    noPadding?: boolean
    chevronHidden?: boolean
    chevronNextToText?: boolean
    chevronClass?: string
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    plain: false,
    reverseColumn: false,
    noPadding: false,
    chevronHidden: false,
    chevronNextToText: false,
    chevronClass: undefined
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
}>()

const expanded = ref(props.modelValue)

const withPadding = computed((): boolean => {
    return !props.reverseColumn && !props.noPadding
})

watch(
    () => props.modelValue,
    (value: boolean) => {
        expanded.value = value
    }
)

const toggle = () => {
    expanded.value = !expanded.value
    emit('update:modelValue', expanded.value)
}
</script>
